import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router-dom';
import { Copy } from 'app/components/Typography';
import useHTMLLinkClickHandler from 'app/hooks/useHTMLLinkClickHandler';
import { BlockType, LinkOptions } from 'app/copied/models/ISIConfig';
import OstroDynamicContent from 'app/components/OstroDynamicContent';
import { cx } from '@twind/core';
import { ChatVersion } from 'app/copied/constants/common';
import useWebchatVersion from 'app/hooks/useWebchatVersion';

import rightChevronIcon from '../../../assets/icons/right-chevron.svg';
import useImportantSafetyInformation from '../../../hooks/useImportantSafetyInformation';
import useTailorTracking from '../hooks/useTailorTracking';

const TailorImportantInformation = forwardRef<
  HTMLButtonElement,
  {
    className?: string;
    showDescription?: boolean;
    ctaText?: string;
  }
>(({ className, showDescription = true, ctaText }, ref) => {
  const navigate = useNavigate();
  const importantSafetyInformation = useImportantSafetyInformation();
  const handleHTMLLinkClick = useHTMLLinkClickHandler();
  const { captureISILinkClick } = useTailorTracking();
  const link = importantSafetyInformation?.links?.length ? importantSafetyInformation?.links[0] : null;
  const isLink = importantSafetyInformation?.block_option === BlockType.Links && !!link;
  const webchatVersion = useWebchatVersion();

  const fontClass = webchatVersion === ChatVersion.v4_1 ? '' : 'font-medium';

  if (
    (!importantSafetyInformation?.preview && !importantSafetyInformation?.links) ||
    !importantSafetyInformation?.display_block
  ) {
    return null;
  }

  const handleClick = () => {
    captureISILinkClick({
      linkUrl: link?.url,
    });
    if (isLink) {
      window.open(link.url, link.link_option === LinkOptions.SameTab ? '_self' : '_blank');
    } else {
      navigate('/tailor/info/isi');
    }
  };

  return (
    <button
      ref={ref}
      id="ostro-tailor-important-information-banner"
      data-testid="important-information-banner"
      className={twMerge(
        'flex py-3 px-4 bg-neutral-200 z-20 rounded-[4px] text-left items-center justify-between w-full',
        className
      )}
      onClick={handleClick}
    >
      <div>
        {!isLink && (
          <div
            className={cx(`~(text-xs text-typography-heading ${fontClass} mb-1)`, {
              'mb-0': !showDescription,
            })}
            dangerouslySetInnerHTML={{
              __html: importantSafetyInformation.preview.title,
            }}
          />
        )}
        {!isLink && showDescription && (
          <div
            className={cx(`~(text-xs text-typography-copy ${fontClass})`, {
              'mb-0': !showDescription,
            })}
            onClick={handleHTMLLinkClick}
            dangerouslySetInnerHTML={{
              __html: importantSafetyInformation.preview.short_preview,
            }}
          />
        )}
        {isLink && (
          <div className={cx(`~(text-xs text-typography-copy ${fontClass})`)}>
            <OstroDynamicContent
              className="[&_p:last-child]:mb-0"
              content={link.name}
            />
          </div>
        )}
      </div>
      <div className="flex items-center shrink-0 text-xs">
        {ctaText && <Copy className={cx(`~(mr-2 ${fontClass} text-xs)`)}>{ctaText}</Copy>}
        <img
          src={rightChevronIcon}
          alt="Right Chevron"
          className="h-2 w-2"
        />
      </div>
    </button>
  );
});

TailorImportantInformation.displayName = 'TailorImportantInformation';

export default TailorImportantInformation;
